"use client";

import {
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  InfoWindow,
  Map,
  MapCameraChangedEvent,
  useMap,
} from "@vis.gl/react-google-maps";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { PinActive, PinDefault, PinHover } from "../../ui/pins";
import PrimaryButton from "@/components/ui/primary-button";
import { FaStar } from "react-icons/fa";
import { MapPin, Phone, Mail, Clock } from "react-feather";
import InlineButton from "@/components/ui/inline-button";
import PolygonComponent from "@/components/ui/polygon";
import { Polygon } from "@/components/locations/find-a-location/map";
import { useWindowSize } from "@/hooks/use-window-size";

export interface Location {
  name: string;
  rating: string;
  address: string;
  phone: string;
  email: string;
  hours: string;
  lat: number;
  lng: number;
  link: string;
}

interface CustomMapProps {
  locations: Location[];
  zoom: number;
  onZoomChanged: (zoom: number) => void;
  serviceAreaPolygons: Polygon[];
  searchedLocation: { lat: number; lng: number } | null;
  nearestLocation: Location | null;
  showServiceArea: boolean;
}

const CustomMap = ({
  locations,
  zoom,
  onZoomChanged,
  serviceAreaPolygons,
  searchedLocation,
  nearestLocation,
  showServiceArea,
}: CustomMapProps) => {
  const map = useMap();
  const size = useWindowSize();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [customZoom, setCustomZoom] = useState(zoom);
  const markerRefs = useRef<
    Array<google.maps.marker.AdvancedMarkerElement | null>
  >(new Array(locations.length).fill(null));

  // Calculate center based on all polygon coordinates
  const center = useMemo(() => {
    const allCoords = serviceAreaPolygons.flatMap(
      (polygon) => polygon.coordinates
    );
    if (allCoords.length === 0)
      return {
        lat: 37.4419,
        lng: -122.1419,
      };

    const totalLat = allCoords.reduce((sum, [lat, _]) => sum + lat, 0);
    const totalLng = allCoords.reduce((sum, [_, lng]) => sum + lng, 0);

    return {
      lat: totalLat / allCoords.length,
      lng: totalLng / allCoords.length,
    };
  }, [serviceAreaPolygons]);

  // Set initial zoom to show the whole service area
  const initialZoom = 9; // Adjust this value to fit your service area

  const getPinSize = (zoom: number) => {
    if (zoom <= 7) return 48;
    if (zoom <= 9) return 56;
    return 62;
  };

  const handlePinClick = (index: number) => {
    setActiveIndex(index);
    setCustomZoom(18);
  };

  const handleInfoWindowClose = () => {
    const index = activeIndex;
    setActiveIndex(null);
    setCustomZoom(10);
    setActiveIndex(index);
    setTimeout(() => {
      setActiveIndex(null);
    }, 100);
  };

  useEffect(() => {
    if (!map || activeIndex === null) return;

    const targetLocation = locations[activeIndex];
    const mapDiv = map.getDiv();
    const mapWidth = mapDiv.clientWidth;
    const mapHeight = mapDiv.clientHeight;

    const targetPixelPoint = map
      .getProjection()
      ?.fromLatLngToPoint(
        new google.maps.LatLng(targetLocation.lat, targetLocation.lng)
      );

    if (!targetPixelPoint) return;

    const scale = Math.pow(2, map.getZoom() ?? customZoom);
    const deviceFactor = size === "lg" ? 5 : size === "md" ? 32 : 52;
    const offsetX = mapWidth / deviceFactor / scale;
    const offsetY = mapHeight / deviceFactor / scale;

    const newPoint = new google.maps.Point(
      targetPixelPoint.x - offsetX,
      targetPixelPoint.y - offsetY
    );

    const newLatLng = map.getProjection()?.fromPointToLatLng(newPoint);

    if (newLatLng) {
      map.panTo(newLatLng);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, activeIndex, locations, customZoom]);

  const updateMarkerRef = useCallback(
    (index: number, ref: google.maps.marker.AdvancedMarkerElement | null) => {
      markerRefs.current[index] = ref;
    },
    []
  );

  useEffect(() => {
    setCustomZoom(zoom);
  }, [zoom]);

  const handleOnZoomChanged = (event: MapCameraChangedEvent) => {
    const newZoom = event.map.getZoom() ?? customZoom;

    setCustomZoom((prevZoom) => {
      if (prevZoom === newZoom) {
        return prevZoom;
      }
      onZoomChanged(newZoom);
      return newZoom;
    });
  };

  useEffect(() => {
    if (searchedLocation) {
      setCustomZoom(10);
      setTimeout(() => {
        map?.panTo(
          new google.maps.LatLng(searchedLocation.lat, searchedLocation.lng)
        );
      }, 300);
    }

    if (searchedLocation && nearestLocation) {
      setActiveIndex(null);
      setTimeout(() => {
        const nearestLocationIndex = locations.findIndex(
          (loc) =>
            loc.lat === nearestLocation.lat && loc.lng === nearestLocation.lng
        );
        setActiveIndex(nearestLocationIndex >= 0 ? nearestLocationIndex : null);
        setCustomZoom(18);
      }, 700);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedLocation, nearestLocation]);

  return (
    <div className="relative h-full w-full">
      <Map
        mapId={"f5697d6426b6ba2b"}
        style={{
          width: "100%",
          height: "100%",
        }}
        defaultCenter={center}
        zoom={customZoom}
        defaultZoom={initialZoom}
        gestureHandling={"greedy"}
        disableDefaultUI={true}
        onZoomChanged={handleOnZoomChanged}
      >
        {/* Render service area polygons only when showServiceArea is true */}
        {showServiceArea &&
          serviceAreaPolygons.map((polygon, index) => (
            <PolygonComponent
              key={index}
              paths={polygon.coordinates.map(([lat, lng]) => ({ lat, lng }))}
              fillColor="#4CAF50"
              fillOpacity={0.15}
              strokeColor="#4CAF50"
              strokeWeight={2}
              strokeOpacity={0.8}
              draggable={false}
              editable={false}
              visible={true}
            />
          ))}

        {searchedLocation && <AdvancedMarker position={searchedLocation} />}

        {locations.map((location, index) => (
          <AdvancedMarker
            key={`${location.name}-${location.lat}-${location.lng}`}
            position={location}
            onClick={() => handlePinClick(index)}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            anchorPoint={AdvancedMarkerAnchorPoint.BOTTOM_CENTER}
            zIndex={Math.floor(1000 - location.lat * 10)}
            ref={(ref) => updateMarkerRef(index, ref)}
          >
            {activeIndex === index ? (
              <PinActive
                width={getPinSize(customZoom)}
                height={Math.floor(getPinSize(customZoom) * 1.16)}
              />
            ) : hoveredIndex === index ? (
              <PinHover
                width={getPinSize(customZoom)}
                height={Math.floor(getPinSize(customZoom) * 1.16)}
              />
            ) : (
              <PinDefault
                width={getPinSize(customZoom)}
                height={Math.floor(getPinSize(customZoom) * 1.16)}
              />
            )}
          </AdvancedMarker>
        ))}

        {activeIndex !== null && markerRefs.current[activeIndex] && (
          <InfoWindow
            anchor={markerRefs.current[activeIndex]}
            onClose={handleInfoWindowClose}
            headerContent={
              <div className="absolute top-2 flex w-full gap-2">
                <h3 className="flex font-montserrat text-subheading-3-500 text-neutral-600 md:text-heading-4">
                  {locations[activeIndex].name}
                </h3>
                <div className="mt-auto flex gap-1 py-1">
                  <FaStar className="h-[12px] w-[12px] text-primary-400" />
                  <span className="pb-0.25 font-roboto text-body-2-300 text-neutral-800">
                    {locations[activeIndex].rating}
                  </span>
                </div>
              </div>
            }
            minWidth={size === "sm" ? 326 : 300}
            maxWidth={size === "sm" ? 326 : 300}
          >
            <div className="flex w-full flex-col gap-4">
              <div className="flex w-full flex-col gap-2">
                <div className="flex flex-row gap-1 text-sm text-gray-500">
                  <MapPin
                    size={16}
                    className="mt-1 flex max-h-[16px] min-h-[16px] min-w-[16px] max-w-[16px] text-secondary-400"
                  />
                  <span className="flex font-roboto text-body-1-300 text-neutral-500">
                    {locations[activeIndex].address}
                  </span>
                </div>
                <a
                  href={`tel:${locations[activeIndex].phone}`}
                  className="flex flex-row gap-1 text-sm text-gray-500"
                >
                  <Phone
                    size={14}
                    className="mt-1 flex text-secondary-400"
                  />
                  <span className="flex font-roboto text-body-1-300 text-neutral-500">
                    {locations[activeIndex].phone}
                  </span>
                </a>
                <a
                  href={`mailto:${locations[activeIndex].email}`}
                  className="flex flex-row gap-1 text-sm text-gray-500"
                >
                  <Mail
                    size={14}
                    className="mt-1 flex text-secondary-400"
                  />
                  <span className="flex font-roboto text-body-1-300 text-neutral-500">
                    {locations[activeIndex].email}
                  </span>
                </a>
                <div className="flex flex-row gap-1 text-sm text-gray-500">
                  <Clock
                    size={14}
                    className="mt-1 flex text-secondary-400"
                  />
                  <span className="flex font-roboto text-body-1-300 text-neutral-500">
                    {locations[activeIndex].hours}
                  </span>
                </div>
              </div>
              <div className="flex flex-row gap-3 md:gap-4 lg:gap-6">
                <PrimaryButton
                  color="orange"
                  size="sm"
                  className="w-fit"
                  href="/care-assessment"
                >
                  Get Started
                </PrimaryButton>
                <InlineButton
                  color="gray"
                  size="sm"
                  className="w-fit"
                  href={locations[activeIndex].link}
                >
                  Learn More
                </InlineButton>
              </div>
            </div>
          </InfoWindow>
        )}
      </Map>
    </div>
  );
};

export default CustomMap;
